import { CSSProperties, useEffect, useRef } from 'react';
import { SEAT_ACTION_BUBBLE_GAP_PX } from 'constants/gameConstants';
import gsap from 'gsap';
import { TableZ } from 'styles/zIndex';

import { seatCardBandReach } from 'components/Seat/SeatWithPlayer/SeatCards';
import {
  minScaledFontSize,
  SEAT_ACTION_BUBBLE_FONT_SIZE,
  SEAT_ACTION_BUBBLE_LINE_HEIGHT,
  SEAT_ACTION_BUBBLE_PADDING_Y_PX,
  seatAvatarPaintedReach
} from 'components/Seat/styles';

type SeatActionBubbleProps = {
  /** Pre-formatted label from useSeatLogic — "CALL", "STRADDLE (ALL IN)", … */
  label: string;
  /** The action palette's saturated colour; the bubble is filled with it. */
  color?: string;
  isPortrait: boolean;
  /**
   * Open toward the felt's centre instead of to the right. Set for seats on
   * the right-hand side of the table — see the geometry note below.
   */
  opensLeft?: boolean;
};

/**
 * A player's last action, as a speech bubble above their portrait.
 *
 * It used to be a pill stacked UNDER the name and balance, which read as another
 * row of seat data rather than as something the player just did — and it grew
 * the seat downward every time anyone acted. As a bubble it reads as speech: it
 * belongs to the avatar, it sits in otherwise-empty felt above the seat, and it
 * costs the seat card no height at all (the whole thing is an absolute overlay).
 *
 * Geometry notes:
 *  - It clears whichever is TALLER, the avatar's painted top or the hole-card
 *    band (`seatCardBandReach`). The avatar's reach is `seatAvatarPaintedReach`,
 *    NOT the raw pop offset — the avatar is scaled inside its wrapper, so the
 *    two differ by several px and using the wrong one leaves the bubble
 *    visibly floating.
 *
 *    The CARDS are the taller of the two once they flip: a revealed two-card
 *    hand paints at SCALE_FACE_UP against 0.5/0.6 face-down, which lifts the
 *    band's top ~10px — past a bubble floor that only cleared the avatar, and
 *    the bubble is banded ABOVE the cards (TableZ.withinSeatBadge), so it
 *    covered the corner of the revealed hand rather than slipping behind it
 *    (reported 2026-09-17 on a multi-run showdown, where every seat carries a
 *    RUN IT nX bubble at exactly the moment every hand is face up).
 *
 *    Clearing the reveal at ALL times, rather than raising the bubble when the
 *    cards flip, is deliberate — the same call the card band itself makes (see
 *    SEAT_CARD_BAND_LIFT_*). A bubble that jumps on the flip draws more
 *    attention than one sitting a few px higher all hand.
 *  - It opens toward the felt's CENTRE: right of the seat's centre line for
 *    seats on the left of the table, left of it for seats on the right.
 *
 *    It used to open right on every seat, on the reasoning that the hole cards
 *    fan LEFT so the right is free. True, but it ignored the other side of the
 *    seat: a right-hand seat is already clamped as close to the viewport edge
 *    as its own card allows (seatEdgeInsets), so a bubble opening right from
 *    there runs straight off the screen. At the 390px design floor the seat
 *    centre lands at 89.6% and an eight-character label such as STRADDLE
 *    finishes about 16px past the edge — reported 2026-08-26.
 *
 *    Opening inward costs nothing vertically: the bubble's floor clears the
 *    whole card band on either side, so it passes over the cards rather than
 *    through them whichever way it opens.
 */
export const SeatActionBubble = ({
  label,
  color,
  isPortrait,
  opensLeft = false
}: SeatActionBubbleProps) => {
  const bubbleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bubbleRef.current;
    if (!el) return;
    // Pops in like a toast. Deliberately small and quick — a seat can act every
    // few seconds, and anything showier turns a busy table into noise.
    const tween = gsap.fromTo(
      el,
      { opacity: 0, scale: 0.9, y: 4 },
      { opacity: 1, scale: 1, y: 0, duration: 0.22, ease: 'back.out(1.4)' }
    );
    return () => {
      tween.kill();
    };
  }, [label]);

  const fill = color ?? DEFAULT_FILL;

  return (
    <div style={anchorStyle(isPortrait, opensLeft)}>
      <div ref={bubbleRef} style={bubbleStyle(opensLeft)}>
        <span style={labelStyle}>{label}</span>
        <span style={{ ...tailStyle(opensLeft), borderTopColor: fill }} />
        <span style={{ ...bubbleFillStyle, backgroundColor: fill }} />
      </div>
    </div>
  );
};

export default SeatActionBubble;

/** Muted grey for an action with no palette entry — never a bare transparent box. */
const DEFAULT_FILL = 'rgba(80, 80, 80, 0.85)';

/** Distance from the bubble's left edge to the centre of its tail. */
const TAIL_INSET = 12;
const TAIL_SIZE = 6;

/**
 * How far the tail paints BELOW the bubble body — it hangs at
 * `bottom: -(TAIL_SIZE - 1)`, so the body's box is NOT the bubble's lowest
 * painted pixel, and clearance measured from it is short by this much.
 */
const TAIL_DROP = TAIL_SIZE - 1;

/**
 * How far above the seat card's top edge the bubble's BODY sits, chosen so the
 * bubble's lowest PAINTED pixel — the point of the tail, `TAIL_DROP` further
 * down — clears everything the seat paints up there: the avatar's head and the
 * hole cards, revealed or not. Exported so the geometry can be asserted directly.
 *
 * Measuring to the body alone is the trap: the tail still hung ~3px into the top
 * of a revealed hand, which is the overlap this floor exists to prevent.
 */
export const seatActionBubbleFloor = (isPortrait: boolean): number =>
  Math.max(seatAvatarPaintedReach(isPortrait), seatCardBandReach(isPortrait)) +
  TAIL_DROP +
  SEAT_ACTION_BUBBLE_GAP_PX;

/**
 * Sits the bubble's bottom edge just above the seat's tallest furniture, on the
 * seat's centre line, then shifts it left by the tail inset so the TAIL — not
 * the bubble's corner — is what points at the avatar.
 */
const anchorStyle = (isPortrait: boolean, opensLeft: boolean): CSSProperties => ({
  position: 'absolute',
  // Anchoring from the RIGHT edge is what makes the bubble grow leftward:
  // an inline-flex box pinned by its right edge extends away from it.
  ...(opensLeft ? { right: '50%' } : { left: '50%' }),
  bottom: `calc(100% + ${seatActionBubbleFloor(isPortrait)}px)`,
  transform: `translateX(${opensLeft ? TAIL_INSET : -TAIL_INSET}px)`,
  // Above the seat's cards and avatar — the bubble is the newest information on
  // the seat, and it is drawn in airspace nothing else occupies anyway.
  zIndex: TableZ.withinSeatBadge,
  pointerEvents: 'none',
  whiteSpace: 'nowrap'
});

/**
 * The bubble body. The fill is painted by an absolutely-positioned child rather
 * than a background on this element, so the tail can tuck UNDER the body's
 * rounded corner without a seam where the two colours meet.
 */
const bubbleStyle = (opensLeft: boolean): CSSProperties => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  padding: `${SEAT_ACTION_BUBBLE_PADDING_Y_PX}px 8px`,
  borderRadius: '10px',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.45)',
  // Pop out FROM the tail, whichever end it is on.
  transformOrigin: opensLeft ? `calc(100% - ${TAIL_INSET}px) 100%` : `${TAIL_INSET}px 100%`
});

const bubbleFillStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  borderRadius: '10px',
  zIndex: 0
};

const labelStyle: CSSProperties = {
  position: 'relative',
  zIndex: 1,
  fontSize: minScaledFontSize(SEAT_ACTION_BUBBLE_FONT_SIZE),
  fontWeight: 800,
  color: '#ffffff',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  lineHeight: SEAT_ACTION_BUBBLE_LINE_HEIGHT,
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.35)'
};

/** Downward triangle joining the bubble to the head below it. */
const tailStyle = (opensLeft: boolean): CSSProperties => ({
  position: 'absolute',
  bottom: `${-TAIL_SIZE + 1}px`,
  // The tail stays over the avatar, so it moves to whichever end is nearest
  // the seat's centre line.
  ...(opensLeft
    ? { right: `${TAIL_INSET - TAIL_SIZE}px` }
    : { left: `${TAIL_INSET - TAIL_SIZE}px` }),
  width: 0,
  height: 0,
  borderLeft: `${TAIL_SIZE}px solid transparent`,
  borderRight: `${TAIL_SIZE}px solid transparent`,
  borderTopWidth: `${TAIL_SIZE}px`,
  borderTopStyle: 'solid',
  zIndex: 0
});
